import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../services/apiClient';

const DISMISS_KEY = 'paymentStatusBannerDismissedAt';
// 6h dismiss — short enough that the user is reminded multiple times a day
// (this is intentional: failed payments cost real money, the banner SHOULD nag).
const DISMISS_DURATION_MS = 6 * 60 * 60 * 1000;

/**
 * Top-of-page banner that surfaces a failing or canceled subscription payment.
 *
 * Lifecycle:
 *   - past_due  → Stripe is retrying; user keeps access until final retry fails.
 *   - canceled  → Stripe gave up; access revoked, user can resubscribe.
 *   - Hidden when paymentStatus="active" or no subscription.
 *   - Dismiss hides it for 6h (intentionally aggressive — failed-payment
 *     recovery is revenue-critical).
 */
const PaymentStatusBanner = () => {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    try {
      const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION_MS) {
        setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    apiClient
      .get('/api/subscription/payment-status')
      .then((res) => { if (!cancelled) setStatus(res.data); })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[PaymentStatusBanner] Could not fetch status:', err?.response?.status);
          setStatus(null);
        }
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleUpdatePayment = async () => {
    if (status?.lastFailedInvoiceUrl) {
      window.open(status.lastFailedInvoiceUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setOpeningPortal(true);
    try {
      const res = await apiClient.post('/api/subscription/customer-portal');
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error('[PaymentStatusBanner] Customer portal error:', err);
      alert('Nu am putut deschide portalul de plata. Mergi la pagina Cont.');
    } finally {
      setOpeningPortal(false);
    }
  };

  if (!isAuthenticated || !status || dismissed) return null;
  if (status.paymentStatus !== 'past_due' && status.paymentStatus !== 'canceled') return null;

  const isCanceled = status.paymentStatus === 'canceled';

  // Format next retry date (with "in X zile" prefix for past_due — adds urgency)
  let nextRetryStr = null;
  let daysUntilRetry = null;
  if (status.nextPaymentRetryDate) {
    try {
      const retryDate = new Date(status.nextPaymentRetryDate);
      const diffMs = retryDate.getTime() - Date.now();
      daysUntilRetry = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      nextRetryStr = retryDate.toLocaleDateString('ro-RO', {
        day: 'numeric', month: 'long',
      });
    } catch { /* ignore */ }
  }

  // Two distinct visual treatments — past_due is loud amber, canceled is alarming red.
  const theme = isCanceled
    ? {
        gradient: 'from-red-600 via-rose-600 to-red-700',
        accent: 'bg-red-900/30',
        iconBg: 'bg-white/20',
        chip: 'bg-red-900/30 text-white',
        button: 'bg-white text-red-700 hover:bg-red-50',
        dismissBtn: 'text-red-100 hover:text-white hover:bg-white/10',
        pulseRing: '',
      }
    : {
        gradient: 'from-amber-500 via-orange-500 to-amber-600',
        accent: 'bg-amber-900/20',
        iconBg: 'bg-white/20',
        chip: 'bg-amber-900/30 text-white',
        button: 'bg-white text-amber-700 hover:bg-amber-50',
        dismissBtn: 'text-amber-100 hover:text-white hover:bg-white/10',
        pulseRing: 'animate-ping',
      };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`relative overflow-hidden bg-gradient-to-r ${theme.gradient} text-white shadow-lg`}
      style={{ animation: 'paymentBannerSlideDown 0.4s cubic-bezier(0.2, 0.9, 0.3, 1)' }}
    >
      <style>{`
        @keyframes paymentBannerSlideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes paymentBannerPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.85; }
        }
      `}</style>

      {/* Subtle diagonal stripe pattern as background texture */}
      <div
        className={`absolute inset-0 ${theme.accent} opacity-30 pointer-events-none`}
        style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(255,255,255,.04) 12px, rgba(255,255,255,.04) 24px)',
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Icon — pulsing ring for past_due to draw the eye */}
        <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
          <div className="relative shrink-0">
            {!isCanceled && (
              <span className={`absolute inset-0 rounded-full bg-white/40 ${theme.pulseRing}`} aria-hidden />
            )}
            <div className={`relative w-10 h-10 ${theme.iconBg} backdrop-blur-sm rounded-full flex items-center justify-center ring-2 ring-white/30`}>
              {isCanceled ? (
                // Lock icon for canceled
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
                </svg>
              ) : (
                // Triangle warning for past_due
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.875.875 0 100-1.75.875.875 0 000 1.75z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <h2 className="font-bold text-sm sm:text-base tracking-tight">
                {isCanceled ? 'Abonament suspendat' : 'Plata abonamentului a esuat'}
              </h2>
              {!isCanceled && daysUntilRetry !== null && (
                <span className={`text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full ${theme.chip} uppercase tracking-wide`}>
                  {daysUntilRetry === 0
                    ? 'reincercare azi'
                    : daysUntilRetry === 1
                      ? 'reincercare maine'
                      : `${daysUntilRetry} zile pana la reincercare`}
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-white/95 leading-snug">
              {isCanceled ? (
                <>
                  Accesul tau la functionalitatile premium a fost revocat. Reactiveaza abonamentul ca sa continui sa primesti notificari, AI Reports si rapoartele zilnice.
                </>
              ) : (
                <>
                  {status.lastFailureReason && (
                    <span className="font-semibold">{status.lastFailureReason}. </span>
                  )}
                  {nextRetryStr
                    ? <>Stripe reincearca automat pe <strong>{nextRetryStr}</strong>. Pana atunci pastrezi accesul, dar daca nu actualizezi cardul, abonamentul va fi suspendat.</>
                    : <>Actualizeaza cardul cat mai repede ca sa eviti suspendarea abonamentului.</>
                  }
                </>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 self-stretch sm:self-auto">
          <button
            onClick={handleUpdatePayment}
            disabled={openingPortal}
            className={`flex-1 sm:flex-none px-4 py-2 sm:py-2 ${theme.button} text-xs sm:text-sm font-bold rounded-lg shadow-md disabled:opacity-60 transition-all hover:shadow-lg active:scale-[0.98]`}
          >
            {openingPortal
              ? 'Se deschide...'
              : isCanceled
                ? 'Reactiveaza acum'
                : 'Actualizeaza cardul'}
          </button>
          <button
            onClick={handleDismiss}
            className={`p-2 ${theme.dismissBtn} rounded-lg transition`}
            title="Inchide (reapare in 6 ore)"
            aria-label="Inchide banner"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M4.28 4.22a.75.75 0 011.06 0L10 8.94l4.66-4.72a.75.75 0 111.07 1.06L11.07 10l4.66 4.72a.75.75 0 11-1.07 1.06L10 11.06l-4.66 4.72a.75.75 0 11-1.07-1.06L8.93 10 4.28 5.28a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusBanner;
